import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select } from "antd";
import {
  IKlearlyAdmin,
  AdminRole,
  EmployeeType,
} from "../../../../interfaces/klearlyAdmin.interface";
import { useAppDispatch } from "../../../../app/hooks";
import { createAdmin, editAdmin } from "../../adminsSlice";

interface AdminsEditorModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  admin?: IKlearlyAdmin | null;
}

export default function AdminsEditorModal({
  open,
  onClose,
  onSaved,
  admin,
}: AdminsEditorModalProps) {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [saving, setSaving] = useState(false);

  function computeSalesIdFromName(name?: string): string {
    if (!name) return "";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    const cap = (s: string) =>
      s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    if (parts.length === 1) return cap(parts[0]);
    const first = cap(parts[0]);
    const initials = parts
      .slice(1)
      .map((p) => cap(p).charAt(0))
      .join(".");
    return `${first} ${initials}`;
  }

  useEffect(() => {
    if (admin) {
      form.setFieldsValue({
        name: admin.name,
        email: admin.email,
        roles: admin.roles,
        employeeType: admin.employeeType,
        salesId: admin.salesId || computeSalesIdFromName(admin.name),
      });
    } else {
      form.resetFields();
    }
  }, [admin, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (admin) await dispatch(editAdmin({ ...admin, ...values })).unwrap();
      else await dispatch(createAdmin(values)).unwrap();
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={admin ? "Edit Admin" : "Create Admin"}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={admin ? "Save" : "Create"}
      confirmLoading={saving}
      okButtonProps={{ disabled: saving, loading: saving }}
      destroyOnClose
    >
      <Form
        layout="vertical"
        form={form}
        disabled={saving}
        onValuesChange={(changed, all) => {
          const changedName = Object.prototype.hasOwnProperty.call(
            changed,
            "name"
          );
          const changedEmployeeType = Object.prototype.hasOwnProperty.call(
            changed,
            "employeeType"
          );
          const isSales = all.employeeType === "sales";
          if ((changedName || changedEmployeeType) && isSales) {
            const next = computeSalesIdFromName(all.name);
            form.setFieldsValue({ salesId: next });
          }
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Please enter a name" }]}
        >
          <Input placeholder="Full name" />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            {
              required: true,
              type: "email",
              message: "Please enter a valid email",
            },
          ]}
        >
          <Input placeholder="email@example.com" />
        </Form.Item>
        <Form.Item
          label="Employee Type"
          name="employeeType"
          rules={[{ required: true, message: "Please select employee type" }]}
        >
          <Select
            options={[
              { label: "sales", value: "sales" },
              { label: "operations", value: "operations" },
              { label: "support", value: "support" },
              { label: "general", value: "general" },
              { label: "coreTech", value: "coreTech" },
            ]}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.employeeType !== cur.employeeType}
        >
          {({ getFieldValue }) =>
            getFieldValue("employeeType") === "sales" ? (
              <Form.Item
                label="Sales ID"
                name="salesId"
                rules={[{ required: true, message: "Please enter Sales ID" }]}
              >
                <Input disabled={true} placeholder="e.g. SR-1234" />
              </Form.Item>
            ) : null
          }
        </Form.Item>
        <Form.Item
          label="Roles"
          name="roles"
          rules={[
            { required: true, message: "Please select at least one role" },
          ]}
        >
          <Select
            mode="multiple"
            options={[
              { label: "Super Admin", value: "SuperAdmin" },
              { label: "Admin", value: "Admin" },
              { label: "Sales", value: "Sales" },
              { label: "Super Sales Admin", value: "SuperSalesAdmin" },
              { label: "Terminal Coupling", value: "TerminalCoupling" },
              { label: "Analytics", value: "Analytics" },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
